import React from "react";
import {
  Typography as MuiTypography,
  TypographyProps as MuiTypographyProps,
} from "@mui/material";
import { colors } from "./colors";

export type TypographyVariant =
  | "headingDisplay"
  | "headingXLarge"
  | "headingLarge"
  | "headingMedium"
  | "subheading"
  | "body"
  | "bodyLight"
  | "button"
  | "caption";

export type TypographyColor =
  | "primary"
  | "secondary"
  | "error"
  | "white"
  | "inherit";

interface TypographyProps extends Omit<
  MuiTypographyProps,
  "variant" | "color"
> {
  variant?: TypographyVariant;
  color?: TypographyColor;
  children: React.ReactNode;
}

const variantStyles: Record<
  TypographyVariant,
  {
    fontSize: string;
    fontWeight: number;
    lineHeight: string;
    fontFamily: string;
  }
> = {
  headingDisplay: {
    fontSize: "2.25rem",
    fontWeight: 600,
    lineHeight: "1.25",
    fontFamily: "Outfit, sans-serif",
  }, // 36px
  headingXLarge: {
    fontSize: "1.75rem",
    fontWeight: 700,
    lineHeight: "1.32",
    fontFamily: "Outfit, sans-serif",
  },
  headingLarge: {
    fontSize: "1.5rem",
    fontWeight: 600,
    lineHeight: "1.42",
    fontFamily: "Outfit, sans-serif",
  },
  headingMedium: {
    fontSize: "1.125rem",
    fontWeight: 600,
    lineHeight: "1.5",
    fontFamily: "Outfit, sans-serif",
  },
  subheading: {
    fontSize: "1rem",
    fontWeight: 400,
    lineHeight: "1.5",
    fontFamily: "Outfit, sans-serif",
  },
  body: {
    fontSize: "0.875rem",
    fontWeight: 400,
    lineHeight: "1.5",
    fontFamily: "Outfit, sans-serif",
  },
  bodyLight: {
    fontSize: "0.875rem",
    fontWeight: 300,
    lineHeight: "1.18",
    fontFamily: "Outfit, sans-serif",
  },
  button: {
    fontSize: "1rem",
    fontWeight: 600,
    lineHeight: "1.25",
    fontFamily: "Outfit, sans-serif",
  },
  caption: {
    fontSize: "0.75rem",
    fontWeight: 400,
    lineHeight: "1.58",
    fontFamily: "Outfit, sans-serif",
  },
};

const colorMap: Record<TypographyColor, string> = {
  primary: colors.primary[500],
  secondary: colors.text.secondary, // #E2E6EE
  error: colors.error[400], // #EE1D52
  white: colors.neutral.white,
  inherit: "inherit",
};

const Typography: React.FC<TypographyProps> = ({
  variant = "body",
  color,
  children,
  sx,
  ...props
}) => {
  const variantStyle = variantStyles[variant];
  const resolvedColor = color ? colorMap[color] : undefined;

  return (
    <MuiTypography
      {...props}
      sx={{
        fontFamily: variantStyle.fontFamily,
        fontSize: variantStyle.fontSize,
        fontWeight: variantStyle.fontWeight,
        lineHeight: variantStyle.lineHeight,
        color: resolvedColor,
        ...sx,
      }}
    >
      {children}
    </MuiTypography>
  );
};

export default Typography;
